import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectActiveClickToIdentifyServices } from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import { getQueryableLayers } from '@rsos/reduxDataStore/src/features/gisServices/layer-loaders/buildLayerMap';
import LayerSelectionMenu from './LayerSelectionMenu';
import { showMapClickInfo, hideMapClickInfo } from './clickToIdentifySlice';
import { FEATURE_SERVICE, MAP_SERVICE } from './constants';
import { queryFeatureLayer, identifyMapService } from './layerHelpers';
import type {
  MapPoint,
  MapClickEvent,
  FeatureData,
  MapClickInfo,
  LayerSelectionResult,
  LayerSelectionMenuData,
  RootState,
  ClickToIdentifyGLProps,
  HelperFunctionResult,
} from './types';

const formatMapClickInfo = (
  event: { mapPoint: MapPoint },
  result: Partial<FeatureData>,
): MapClickInfo => {
  return {
    latitude: event.mapPoint.latitude,
    longitude: event.mapPoint.longitude,
    featureData: {
      layerName: result.layerName || '',
      layerId: result.layerId || '',
      attributes: result.attributes || {},
      fields: Object.keys(result.attributes || {}).map(key => ({
        name: key,
        alias: key,
      })),
      parentFeatureLayerID: result.layerId || result.layerName || '',
      isLoading: result.isLoading || false,
      isCluster: result.isCluster || false,
    },
  };
};

const getConfiguredServiceCategory = (
  service: LayerSelectionResult['data'],
): string => {
  const category = service.attributes?.gis_service_category;
  return category === FEATURE_SERVICE || category === MAP_SERVICE
    ? category
    : '';
};

const hasQueryFeatures = (
  layer: unknown,
): layer is FeatureLayer & { queryFeatures: FeatureLayer['queryFeatures'] } => {
  return (
    !!layer &&
    typeof layer === 'object' &&
    'queryFeatures' in layer &&
    typeof layer.queryFeatures === 'function'
  );
};

type ClickToIdentifyLayer = {
  service: LayerSelectionResult['data'];
  gisLayer?: { type?: string } | null;
};

const getLayerServiceCategory = (
  layer: ClickToIdentifyLayer,
  queryableLayer?: unknown,
): string => {
  const configuredCategory = getConfiguredServiceCategory(layer.service);
  if (configuredCategory) return configuredCategory;

  if (layer.gisLayer?.type === 'map-image') {
    return MAP_SERVICE;
  }

  if (hasQueryFeatures(queryableLayer) || hasQueryFeatures(layer.gisLayer)) {
    return FEATURE_SERVICE;
  }

  return '';
};

/**
 * Identify and analyze points on the Esri map
 */
const ClickToIdentifyGL = forwardRef<HTMLElement, ClickToIdentifyGLProps>(
  ({ mapInstance, identifyTolerance = 3, queryBufferMeters = 3 }, ref) => {
    const dispatch = useDispatch();
    const mapRef = typeof ref === 'object' && ref?.current ? ref.current : null;
    const view = mapInstance?.view;

    const activeLayers = useSelector(selectActiveClickToIdentifyServices);
    const mapClickInfo = useSelector(
      (state: RootState) => state.clickToIdentify?.mapClickInfo,
    );

    const [layerSelectionMenu, setLayerSelectionMenu] =
      useState<LayerSelectionMenuData | null>(null);

    const hasActiveLayers = activeLayers.length > 0;

    const handlePopupClose = useCallback(() => {
      dispatch(hideMapClickInfo());
      setLayerSelectionMenu(null);
    }, [dispatch]);

    const handleLayerSelect = useCallback(
      async (selectedResult: LayerSelectionResult) => {
        if (!view) return;
        setLayerSelectionMenu(null);
        // Immediately show popover with loading state
        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult?.data?.id || ''),
                attributes: {},
                isLoading: true, // Add loading flag
              },
            ),
          ),
        );

        const attributesBySource: Record<string, Record<string, unknown>[]> =
          {};

        for (const layer of activeLayers) {
          if (!layer.gisLayer) continue;
          // Get all visible sublayers (or the layer itself if no sublayers)
          const queryableLayers = getQueryableLayers(layer.gisLayer);

          for (const queryableLayer of queryableLayers) {
            const layerServiceCategory = getLayerServiceCategory(
              layer,
              queryableLayer,
            );
            const selectedLayerType =
              selectedResult.layerType || layerServiceCategory;
            if (
              selectedResult.data.id === layer.service.id &&
              selectedLayerType === FEATURE_SERVICE &&
              layerServiceCategory === FEATURE_SERVICE
            ) {
              const queryResult = await queryFeatureLayer({
                layer: queryableLayer as FeatureLayer,
                longitude: selectedResult?.mapPoint?.longitude || 0,
                latitude: selectedResult?.mapPoint?.latitude || 0,
                fallbackLayerName: layer.title,
                bufferMeters: queryBufferMeters,
              });

              if (queryResult) {
                if (!attributesBySource[queryResult.sourceTitle]) {
                  attributesBySource[queryResult.sourceTitle] = [];
                }
                attributesBySource[queryResult.sourceTitle].push(
                  queryResult.attributes,
                );
              }
            } else if (
              selectedResult.data.id === layer.service.id &&
              selectedLayerType === MAP_SERVICE &&
              layerServiceCategory === MAP_SERVICE
            ) {
              const identifyResult: HelperFunctionResult | null =
                await identifyMapService({
                  url: layer.service.gis_service_url,
                  longitude: selectedResult.mapPoint.longitude,
                  latitude: selectedResult.mapPoint.latitude,
                  view,
                  token: layer.service?.token,
                  tolerance: identifyTolerance,
                  fallbackLayerName: layer.title,
                });

              if (identifyResult) {
                if (!attributesBySource[identifyResult.sourceTitle]) {
                  attributesBySource[identifyResult.sourceTitle] = [];
                }
                attributesBySource[identifyResult.sourceTitle].push(
                  identifyResult.attributes,
                );
              }
            }
          }
        }

        dispatch(
          showMapClickInfo(
            formatMapClickInfo(
              {
                mapPoint: {
                  latitude: selectedResult.latitude,
                  longitude: selectedResult.longitude,
                },
              },
              {
                layerName: selectedResult.data?.display_name,
                layerId: String(selectedResult.data.id),
                attributes: attributesBySource,
                isLoading: false, // Remove loading flag
              },
            ),
          ),
        );
      },
      [dispatch, activeLayers, queryBufferMeters, view, identifyTolerance],
    );

    const handleMapClick = useCallback(
      async (event: MapClickEvent) => {
        if (mapRef) mapRef.style.cursor = 'wait';

        try {
          const results: LayerSelectionResult[] = [];
          const hitMatchedServiceIds = new Set<number>();

          // Use hitTest to check if the click landed on a rendered GIS feature.
          // This lets users click directly on icons (e.g. camera markers) without
          // needing to zoom all the way in — the hit area matches the visual size.
          if (view) {
            const hitResponse = await view.hitTest(event);
            if (hitResponse?.results?.length) {
              for (const hit of hitResponse.results) {
                if (!('graphic' in hit) || !hit.graphic?.layer) continue;

                const hitLayerId = hit.graphic.layer.id;
                const matchedLayer = activeLayers.find(
                  ({ service }) =>
                    `gis-service-${service.service_type}-${service.id}` ===
                    hitLayerId,
                );

                if (
                  matchedLayer &&
                  !hitMatchedServiceIds.has(matchedLayer.service.id)
                ) {
                  hitMatchedServiceIds.add(matchedLayer.service.id);

                  const isClusterHit =
                    (hit.graphic as unknown as { isAggregate?: boolean })
                      .isAggregate === true ||
                    hit.graphic.attributes?.cluster_count != null;

                  if (isClusterHit) {
                    dispatch(
                      showMapClickInfo(
                        formatMapClickInfo(
                          { mapPoint: event.mapPoint },
                          {
                            layerName: matchedLayer.service.display_name,
                            layerId: String(matchedLayer.service.id),
                            attributes: {},
                            isLoading: false,
                            isCluster: true,
                          },
                        ),
                      ),
                    );
                    if (mapRef) mapRef.style.cursor = 'auto';
                    return;
                  }

                  const geom = hit.graphic.geometry as __esri.Point | undefined;
                  const featureLat = geom?.latitude ?? event.mapPoint.latitude;
                  const featureLng =
                    geom?.longitude ?? event.mapPoint.longitude;
                  const featureMapPoint: MapPoint = {
                    latitude: featureLat,
                    longitude: featureLng,
                  };

                  results.push({
                    layerName: matchedLayer.service.display_name,
                    layerId: String(matchedLayer.service.id),
                    feature: {
                      attributes: hit.graphic.attributes || {},
                    },
                    latitude: featureLat,
                    longitude: featureLng,
                    data: matchedLayer.service,
                    layerType: getLayerServiceCategory(matchedLayer),
                    mapPoint: featureMapPoint,
                  });
                }
              }
            }
          }

          // If hitTest matched exactly one feature and there are no other
          // active layers, skip the selection menu and open the popover
          // directly (e.g. clicking a single camera icon).
          if (
            hitMatchedServiceIds.size === 1 &&
            results.length === 1 &&
            activeLayers.length === 1
          ) {
            handleLayerSelect(results[0]);
          } else {
            // Include active layers that weren't matched by hitTest so the
            // user can still pick them from the layer selection menu.
            activeLayers.forEach(layer => {
              const { service } = layer;
              if (!hitMatchedServiceIds.has(service.id)) {
                results.push({
                  layerName: service.display_name,
                  layerId: String(service.id),
                  feature: { attributes: {} },
                  data: service,
                  layerType: getLayerServiceCategory(layer),
                  mapPoint: event?.mapPoint,
                  latitude: event.mapPoint.latitude,
                  longitude: event.mapPoint.longitude,
                });
              }
            });

            if (results.length === 1) {
              handleLayerSelect(results[0]);
            } else if (results.length > 1) {
              setLayerSelectionMenu({
                results,
                screenPoint: { x: event.x, y: event.y },
              });
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        } finally {
          if (mapRef) mapRef.style.cursor = 'auto';
        }
      },
      [mapRef, activeLayers, handleLayerSelect, view, dispatch],
    );

    useEffect(() => {
      if (!hasActiveLayers || !view) return;

      const mapClickHandler = view.on('click', (event: MapClickEvent) => {
        if (event.button === 2) {
          // right click
          event.stopPropagation();
          event.preventDefault();
          if (mapClickInfo) {
            dispatch(hideMapClickInfo());
          }
          handleMapClick(event);
        } else {
          setLayerSelectionMenu(null);
          dispatch(hideMapClickInfo());
        }
      });

      return () => mapClickHandler?.remove();
    }, [hasActiveLayers, view, handleMapClick, dispatch, mapClickInfo]);

    useEffect(() => {
      if (!hasActiveLayers && layerSelectionMenu) {
        handlePopupClose();
      }
    }, [hasActiveLayers, layerSelectionMenu, handlePopupClose]);

    return layerSelectionMenu ? (
      <LayerSelectionMenu
        results={layerSelectionMenu.results}
        screenPoint={layerSelectionMenu.screenPoint}
        onLayerSelect={handleLayerSelect}
      />
    ) : null;
  },
);

export default ClickToIdentifyGL;
